/**
 * Quatro numeros da biblioteca no escopo escolhido (empresa) e na janela do
 * cabecalho, cada um com a variacao contra o periodo anterior.
 *
 * Decisoes nao obvias:
 *  1. O primeiro cartao e "pagaram depois de um modelo", nao "envios": a
 *     biblioteca existe para recuperar venda. Envio e meio, pagamento e fim.
 *  2. A taxa de pagamento so conta modelos de publico que GERA credito
 *     (carrinho abandonado, plano vencido). Mensagem da vendedora e suporte
 *     pos-pagamento entrariam no denominador sem nunca poder entrar no
 *     numerador, e a taxa cairia por construcao.
 *  3. Os cartoes seguem o filtro de EMPRESA, mas nao a aba nem a busca: sao a
 *     foto da biblioteca, e mudar de aba nao pode mudar o "total".
 *  4. Cada cartao tem a explicacao em termos de negocio no `title` (e em
 *     texto para leitor de tela), e a variacao tem glifo + texto.
 *  5. "Responderam -> vendedoras" tambem so conta os toques da regua (publicos
 *     que geram credito). A mensagem da propria vendedora e o pos-venda falam
 *     com quem JA esta com ela ou ja pagou: somar as respostas deles contaria
 *     a mesma pessoa duas vezes e inflaria o "caiu com a vendedora".
 */

import { KpiCard } from '../../../components/KpiCard';
import { Badge, Icon, type IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, formatRatio } from '../../../lib/format';
import { somar, somarAnterior, taxaBps } from '../desempenho';
import type { MessageTemplate } from '../types';
import { Variacao } from './Variacao';

/**
 * O cartao e o `KpiCard` do kit; aqui so entram a variacao (no `aside`, ao
 * lado do numero) e a dica de negocio, no `title` do involucro e repetida
 * para o leitor de tela. O involucro e `grid` para o cartao esticar e a
 * fileira ficar com a mesma altura.
 */
function Cartao({
  icone,
  rotulo,
  dica,
  valor,
  variacao,
  apoio,
}: {
  readonly icone: IconName;
  readonly rotulo: string;
  readonly dica: string;
  readonly valor: string;
  readonly variacao: React.ReactNode;
  readonly apoio: string;
}): JSX.Element {
  return (
    <div className="grid min-w-0" title={dica}>
      <span className="sr-only">{dica}</span>
      <KpiCard label={rotulo} value={valor} icon={<Icon name={icone} />} aside={variacao} support={apoio} />
    </div>
  );
}

export function ResumoBiblioteca({ lista }: { readonly lista: readonly MessageTemplate[] }): JSX.Element {
  const creditaveis = lista.filter((t) => t.audience === 'checkout_abandonado' || t.audience === 'plano_vencido');
  const credAtual = somar(creditaveis.map((t) => t.performance));
  const credAnterior = somarAnterior(creditaveis);
  const taxa = taxaBps(credAtual.paid, credAtual.sent);
  const taxaAnterior = credAnterior === null ? null : taxaBps(credAnterior.paid, credAnterior.sent);

  const aprovados = lista.filter((t) => t.status === 'aprovado').length;
  const pendentes = lista.filter((t) => t.status === 'pendente').length;
  const rejeitados = lista.filter((t) => t.status === 'rejeitado').length;
  const livres = lista.filter((t) => t.status === 'texto_livre').length;
  const semUso = lista.filter((t) => t.usedIn.length === 0).length;

  return (
    <section aria-label="Resumo da biblioteca no período" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <Cartao
        icone="moeda"
        rotulo="Pagaram depois de um modelo"
        dica="Pessoas que pagaram em até 7 dias depois de receber um modelo, com o crédito no último contato (ADR-001). Cada venda conta uma vez só."
        valor={formatCount(credAtual.paid)}
        variacao={<Variacao atual={credAtual.paid} anterior={credAnterior === null ? null : credAnterior.paid} />}
        apoio={`${formatBRLFromCents(credAtual.recoveredCents)} recuperados (valor pago) · crédito ao último toque`}
      />
      <Cartao
        icone="grafico"
        rotulo="Taxa de pagamento"
        dica="Pagaram ÷ receberam, só nos modelos de carrinho abandonado e plano vencido (os que geram crédito). Mensagem da vendedora e pós-venda ficam de fora."
        valor={taxa === null ? '—' : formatPercentFromBps(taxa)}
        variacao={taxa === null ? <span className="text-xs text-ink-subtle">sem envios no período</span> : <Variacao atual={taxa} anterior={taxaAnterior} tipo="bps" />}
        apoio={`${formatCount(credAtual.sent)} envios e ligações para quem já abandonou ou já venceu`}
      />
      <Cartao
        icone="resposta"
        rotulo="Responderam → vendedoras"
        dica="Quem respondeu a um toque da régua (botão ou digitando) caiu com a vendedora do comercial e a régua parou para essa pessoa. Se ela fechar em até 7 dias, o crédito continua no toque. Respostas às mensagens da própria vendedora e do pós-venda ficam de fora para não contar a mesma pessoa duas vezes."
        valor={formatCount(credAtual.replied)}
        variacao={<Variacao atual={credAtual.replied} anterior={credAnterior === null ? null : credAnterior.replied} />}
        apoio={`${formatRatio(credAtual.replied, credAtual.delivered)} de quem recebeu um toque · ${formatCount(credAtual.paidViaSeller)} fecharam com a vendedora (“fechado por”; o crédito segue do toque)`}
      />
      <Cartao
        icone="modelo"
        rotulo="Modelos na biblioteca"
        dica="Globais (operados por nós para todas as empresas) mais os de cada empresa, no escopo escolhido. Arquivados na prévia não contam."
        valor={formatCount(lista.length)}
        variacao={
          <span className="flex flex-wrap gap-1.5">
            {pendentes > 0 ? (
              <Badge tone="warning" dot>
                {pendentes} pendente{pendentes === 1 ? '' : 's'}
              </Badge>
            ) : null}
            {rejeitados > 0 ? (
              <Badge tone="critical" dot>
                {rejeitados} rejeitado{rejeitados === 1 ? '' : 's'}
              </Badge>
            ) : null}
            {pendentes === 0 && rejeitados === 0 ? (
              <Badge tone="good" dot>
                Nada parado na Meta
              </Badge>
            ) : null}
          </span>
        }
        apoio={`${formatCount(aprovados)} aprovados · ${formatCount(livres)} texto livre · ${formatCount(semUso)} sem uso`}
      />
    </section>
  );
}
